import { LoadingIcon } from '@blocksuite/affine/components/icons';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import type { LocaleService } from '@blocksuite/affine/shared/services';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { PlusIcon } from '@blocksuite/icons/lit';
import { flip, offset } from '@floating-ui/dom';
import { css, html } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import type { ChatChip, DocDisplayConfig } from '../ai-chat-chips';
import type { SearchMenuConfig } from './type';

export class AIChatAddContext extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .ai-chat-add-context {
      display: flex;
      flex-shrink: 0;
      flex-grow: 0;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .ai-chat-add-context[data-disabled='true'] {
      cursor: wait;
      opacity: 0.6;
    }
  `;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor locale: LocaleService | null | undefined;

  @property({ attribute: false })
  accessor addChip!: (chip: ChatChip) => Promise<void>;

  @property({ attribute: false })
  accessor addImages!: (images: File[]) => void;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor portalContainer: HTMLElement | null = null;

  @property({ attribute: false })
  accessor disabled = false;

  @state()
  private accessor isAdding = false;

  @query('.ai-chat-add-context')
  accessor addButton!: HTMLDivElement;

  private abortController: AbortController | null = null;

  override render() {
    return html`
      <div
        class="ai-chat-add-context"
        data-disabled=${this.isDisabled}
        aria-disabled=${this.isDisabled}
        data-testid="chat-panel-with-button"
        @click=${this.toggleAddDocMenu}
      >
        ${this.isDisabled ? LoadingIcon() : PlusIcon()}
      </div>
    `;
  }

  private readonly toggleAddDocMenu = () => {
    if (this.isDisabled) return;

    if (this.abortController) {
      this.abortController.abort();
      return;
    }

    this.abortController = new AbortController();
    this.abortController.signal.addEventListener('abort', () => {
      this.abortController = null;
    });

    createLitPortal({
      template: html`
        <chat-panel-add-popover
          .docId=${this.docId}
          .independentMode=${this.independentMode}
          .locale=${this.locale}
          .addChip=${this.addChipWithPending}
          .addImages=${this.addImages}
          .searchMenuConfig=${this.searchMenuConfig}
          .docDisplayConfig=${this.docDisplayConfig}
          .abortController=${this.abortController}
        ></chat-panel-add-popover>
      `,
      portalStyles: {
        zIndex: 'var(--affine-z-index-popover)',
      },
      container: this.portalContainer ?? document.body,
      computePosition: {
        referenceElement: this.addButton,
        placement: 'top-start',
        middleware: [offset({ crossAxis: -30, mainAxis: 8 }), flip()],
        autoUpdate: { animationFrame: true },
      },
      abortController: this.abortController,
      closeOnClickAway: true,
    });
  };

  private get isDisabled() {
    return this.disabled || this.isAdding;
  }

  private readonly addChipWithPending = async (chip: ChatChip) => {
    if (this.isDisabled) return;

    this.isAdding = true;
    try {
      await this.addChip(chip);
    } finally {
      this.isAdding = false;
    }
  };
}
